/**
 * titulo-pestana.js — Título de la PESTAÑA de una página del builder: el NOMBRE de la empresa.
 *
 * Espejo EXACTO de core_services/titulo_pestana.py (tests/test_titulo_pestana.py pasa el MISMO
 * fixture por los dos). Lo usa la SPA pública en vivo al navegar entre páginas
 * (`pages/viewer.js`, rama `window.IS_PUBLIC`): el `<title>` inicial lo pone el servidor con la
 * misma regla (`views._meta_spa_publica`) y el del publicado, `views._preparar_publicacion`.
 *
 * Regla: la home (y sus espejos `home-xx`) → «Empresa»; el resto → «Página · Empresa» (solo
 * «Empresa» si la página no tiene título o se llama igual); sin nombre → el título, limpio.
 * El nombre: `negocio.nombre` (la ficha) y, sin ella, el `logo`/`brand` del primer `header` (el de
 * la home, luego el de la primera página, luego el de cualquiera).
 *
 * Módulo PURO, node-testeable, compatible con node 12 (sin `?.`, `??` ni `replaceAll`).
 */

export const TOPE = 120;
export const SEPARADOR = ' · ';

// Lineal: no cruza otro `<` (con `<[^>]+>`, miles de `<` sin `>` bloqueaban la pestaña al navegar).
const ETIQUETA = /<[^<>]+>/g;
const ENTIDAD = /&(amp|lt|gt|quot|#39|apos|nbsp);/g;
const ENTIDADES = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

/** Texto plano de una línea, sin `<`/`>` y con tope (en caracteres, no en unidades UTF-16). */
export function limpiar(texto, tope) {
    if (typeof texto !== 'string') return '';
    const max = typeof tope === 'number' ? tope : TOPE;
    let t = texto.replace(ETIQUETA, ' ');
    t = t.replace(ENTIDAD, function (m, nombre) { return ENTIDADES[nombre]; });
    t = t.replace(/[<>]/g, ' ');
    t = t.replace(/\s+/g, ' ').trim();
    return Array.from(t).slice(0, max).join('').trim();
}

function logoDeHeader(pagina) {
    const bloques = (pagina && Array.isArray(pagina.blocks)) ? pagina.blocks : [];
    for (let i = 0; i < bloques.length; i++) {
        const b = bloques[i];
        if (b && typeof b === 'object' && b.type === 'header') {
            const p = (b.props && typeof b.props === 'object') ? b.props : {};
            return limpiar(p.logo) || limpiar(p.brand);
        }
    }
    return '';
}

/** El nombre de la empresa (ver la cabecera). */
export function nombreDelSitio(siteData) {
    try {
        if (!siteData || typeof siteData !== 'object') return '';
        const negocio = siteData.negocio;
        if (negocio && typeof negocio === 'object') {
            const nombre = limpiar(negocio.nombre);
            if (nombre) return nombre;
        }
        const paginas = (Array.isArray(siteData.pages) ? siteData.pages : [])
            .filter(function (p) { return p && typeof p === 'object' && !Array.isArray(p); });
        const home = paginas.filter(function (p) { return p.id === 'home'; });
        const orden = home.concat(paginas.slice(0, 1), paginas);
        const vistas = new Set();   // cada página, una vez (como en Python)
        for (let i = 0; i < orden.length; i++) {
            if (vistas.has(orden[i])) continue;
            vistas.add(orden[i]);
            const nombre = logoDeHeader(orden[i]);
            if (nombre) return nombre;
        }
        return '';
    } catch (e) {
        return '';
    }
}

/** ¿Es la portada? `id === 'home'`, su espejo `home-xx` (xx en `langCodes`) o `_base === 'home'`. */
export function esHome(pagina, langCodes) {
    if (!pagina || typeof pagina !== 'object') return false;
    const pid = pagina.id;
    if (pid === 'home' || pagina._base === 'home') return true;
    return typeof pid === 'string' && pid.indexOf('home-') === 0
        && (langCodes || []).indexOf(pid.slice(5)) !== -1;
}

/** El `<title>` de `pagina` dentro de `siteData`. `langCodes`: los idiomas del sitio (SSOT
 *  languages.py → window.NUBE3I_LANGS). Nunca lanza. */
export function tituloPestana(siteData, pagina, langCodes) {
    try {
        const titulo = (pagina && typeof pagina === 'object') ? limpiar(pagina.title) : '';
        const nombre = nombreDelSitio(siteData);
        if (!nombre) return titulo;
        if (esHome(pagina, langCodes) || !titulo || titulo.toLowerCase() === nombre.toLowerCase()) return nombre;
        return titulo + SEPARADOR + nombre;
    } catch (e) {
        return '';
    }
}
